"use client"; 

import React, { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { Home, Search, Store, User, LogIn, LogOut, LucideIcon, ShoppingBag } from "lucide-react";
import { cn } from '../../lib/utils';
import { useCart } from '../../contexts/CartContext';

interface NavItem {
  label: string;
  icon: LucideIcon;
  path: string;
  onClick?: () => void;
}

function FloatingNav() {
  const [position, setPosition] = useState({
    left: 0,
    width: 0,
    opacity: 0,
    height: 0,
  });
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut, isFlorist, isAdmin, isCustomer } = useAuth();
  const { itemCount } = useCart();

  // Check if we're on mobile
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  // Handle scroll behavior on mobile
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY <= 20) {
        // Always show when at the top of the page
        setIsVisible(true);
      } else if (currentScrollY < lastScrollY) {
        // Show when scrolling up
        setIsVisible(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 50) {
        // Hide when scrolling down beyond a threshold
        setIsVisible(false);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const handleDashboardClick = () => {
    if (isFlorist) {
      navigate('/florist-dashboard');
    } else if (isAdmin) {
      navigate('/admin');
    } else if (isCustomer) {
      navigate('/customer-dashboard');
    } else {
      // Default fallback - should not reach here normally
      navigate('/account');
    }
  };

  const getNavItems = (): NavItem[] => {
    const items: NavItem[] = [
      { label: "HOME", icon: Home, path: "/" },
      { label: "SEARCH", icon: Search, path: "/search?from=nav" },
      { label: "CART", icon: ShoppingBag, path: "/cart" },
    ];

    if (user) {
      // Add Dashboard with dynamic navigation based on role
      items.push({ 
        label: "DASHBOARD", 
        icon: isFlorist ? Store : User, 
        path: "#",
        onClick: handleDashboardClick
      });
      
      items.push({ 
        label: "SIGN OUT", 
        icon: LogOut, 
        path: "#",
        onClick: handleSignOut 
      });
    } else {
      items.push({ 
        label: "SIGN IN / SIGN UP", 
        icon: LogIn, 
        path: "/auth" 
      });
    }

    return items;
  };

  return (
    <>
      {/* Desktop Navigation - Exactly as it was originally */}
      <nav className="fixed top-6 left-1/2 -translate-x-1/2 z-[100] hidden md:block">
        <ul
          className="relative mx-auto flex w-fit rounded-full border border-[#4A4F41]/20 bg-[#FAF7F0] shadow-lg"
          onMouseLeave={() => setPosition((pv) => ({ ...pv, opacity: 0 }))}
        >
          {getNavItems().map((item) => (
            <Tab 
              key={item.label} 
              setPosition={setPosition}
              onClick={() => item.onClick ? item.onClick() : navigate(item.path)}
              isMobile={false}
            >
              <div className="relative">
                <item.icon className="h-4 w-4" />
                {item.label === "CART" && itemCount > 0 && (
                  <span className="absolute -top-2 -right-2.5 bg-[#843329] text-white text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center">
                    {itemCount > 9 ? '9+' : itemCount}
                  </span>
                )}
              </div>
              <span className="ml-2">{item.label}</span>
            </Tab>
          ))}

          <Cursor position={position} />
        </ul>
      </nav>

      {/* Mobile Navigation - Bottom of screen with scroll behavior */}
      <nav 
        className={cn(
          "fixed bottom-0 left-0 right-0 w-full z-[100] md:hidden",
          "transition-transform duration-300 ease-in-out",
          !isVisible ? "translate-y-full" : "translate-y-0" 
        )}
      >
        <ul className="relative w-full flex justify-around bg-white border-t border-gray-200 shadow-md">
          {getNavItems().map((item) => (
            <Tab 
              key={item.label} 
              setPosition={setPosition}
              onClick={() => item.onClick ? item.onClick() : navigate(item.path)}
              isMobile={true}
            >
              <div className="relative">
                <item.icon className="h-5 w-5" />
                {item.label === "CART" && itemCount > 0 && (
                  <span className="absolute -top-1.5 -right-2 bg-[#843329] text-white text-[9px] font-bold rounded-full h-4 w-4 flex items-center justify-center">
                    {itemCount > 9 ? '9+' : itemCount}
                  </span>
                )}
              </div>
              <span className="text-[10px] mt-1">{item.label}</span>
            </Tab>
          ))}
        </ul>
      </nav>
    </>
  );
}

const Tab = ({
  children,
  setPosition,
  onClick,
  isMobile,
}: {
  children: React.ReactNode;
  setPosition: any;
  onClick: () => void;
  isMobile: boolean;
}) => {
  const ref = useRef<HTMLLIElement>(null);
  return (
    <li
      ref={ref}
      onMouseEnter={() => {
        if (!ref.current || isMobile) return;

        const { width, height } = ref.current.getBoundingClientRect();
        setPosition({
          width,
          height,
          opacity: 1,
          left: ref.current.offsetLeft,
        });
      }}
      onClick={onClick}
      className={cn(
        "relative z-10 block cursor-pointer",
        isMobile 
          ? "flex flex-col items-center py-2" 
          : "px-3 py-2 text-xs uppercase text-[#4A4F41] flex items-center hover:text-[#4A4F41]/80 md:px-5 md:py-2.5"
      )}
    >
      {children}
    </li>
  );
};

const Cursor = ({ position }: { position: any }) => {
  return (
    <motion.li
      animate={position}
      className="absolute inset-0 z-0 rounded-full bg-[#FFB347]/40"
      style={{
        width: position.width,
        height: position.height,
      }}
    />
  );
};

export default FloatingNav; 